// Pricing calculators: P03 Discount Damage, P04 Price Increase, P09 Break-even.
import { useMemo } from "react";
import { discountDamage, priceIncrease, breakEven, inr, pct } from "./engines";
import { Field, SliderField, ResultRow, Note, ToolSection, useNumbers } from "./ui";

export function ToolP03() {
  const [v, set] = useNumbers({ price: 1000, variableCost: 700, discountPct: 10, quantity: 100 });
  const r = useMemo(() => discountDamage(v), [v]);
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-5">
        <Field label="Current selling price per unit" prefix="₹" value={v.price} onChange={set("price")} testid="p03-price" />
        <Field label="Variable cost per unit" hint="Product, packaging, delivery — costs that scale per unit." prefix="₹" value={v.variableCost} onChange={set("variableCost")} testid="p03-cost" />
        <SliderField label="Proposed discount" value={v.discountPct} onChange={set("discountPct")} min={0} max={30} testid="p03-discount" />
        <Field label="Units you expect to sell (period)" value={v.quantity} onChange={set("quantity")} testid="p03-qty" />
        <Note>Discounts come straight out of contribution. The curve shows how much extra volume each discount point demands.</Note>
      </div>
      <div>
        <div className="border border-zinc-200 bg-white p-6">
          <div className="overline">Contribution per unit</div>
          <div className="mt-3 space-y-0">
            <ResultRow label={`Before discount (at ${inr(Number(v.price) || 0)})`} value={`${inr(r.contributionBefore)} (${pct(r.contributionPctBefore)})`} />
            <ResultRow label={`After ${v.discountPct}% discount (at ${inr(r.priceNew)})`} value={`${inr(r.contributionAfter)} (${pct(r.contributionPctAfter)})`} tone={r.contributionAfter < r.contributionBefore ? "bad" : undefined} />
            {r.impossible ? (
              <div className="mt-4 text-sm text-red-700 font-medium" data-testid="p03-impossible">
                At this discount, contribution turns zero or negative — no finite additional volume can preserve your current total contribution.
              </div>
            ) : (
              <ResultRow strong label="Volume needed to preserve total contribution" value={`${r.requiredMultiplier}× (${r.extraUnitsNeededPct}% more units)`} />
            )}
          </div>
        </div>
        <ToolSection title="The discount curve" testid="p03-curve">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-zinc-500 border-b border-black/10">
                <th className="py-2 font-medium">Discount</th>
                <th className="py-2 font-medium">Contribution / unit</th>
                <th className="py-2 font-medium">Volume needed</th>
              </tr>
            </thead>
            <tbody>
              {r.curve.filter((_, i) => i % 2 === 0).map((c) => (
                <tr key={c.discount} className={`border-b border-black/5 ${c.discount === Number(v.discountPct) ? "bg-zinc-50 font-medium" : ""}`}>
                  <td className="py-2">{c.discount}%</td>
                  <td className="py-2 tabular-nums">{inr(c.contribution)}</td>
                  <td className="py-2 tabular-nums">{c.required === null ? "Impossible" : `${c.required}×`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ToolSection>
      </div>
    </div>
  );
}

export function ToolP04() {
  const [v, set] = useNumbers({ price: 100, variableCost: 60, units: 1000, increasePct: 10 });
  const r = useMemo(() => priceIncrease(v), [v]);
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-5">
        <Field label="Current price per unit" prefix="₹" value={v.price} onChange={set("price")} testid="p04-price" />
        <Field label="Variable cost per unit" prefix="₹" value={v.variableCost} onChange={set("variableCost")} testid="p04-cost" />
        <Field label="Current monthly units" value={v.units} onChange={set("units")} testid="p04-units" />
        <SliderField label="Proposed price increase" value={v.increasePct} onChange={set("increasePct")} min={0} max={30} testid="p04-increase" />
        <Note>The tolerable-loss number assumes demand responds at all. Use the elasticity sensitivity to see how wrong you can afford to be — then validate with a pricing experiment.</Note>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">Contribution impact</div>
        <div className="mt-3">
          <ResultRow label="Contribution per unit (before → after)" value={`${inr(r.contributionBefore)} → ${inr(r.contributionAfter)}`} />
          <ResultRow label="Monthly contribution at current volume" value={`${inr(r.totalBefore)} → ${inr(r.totalAfterSameVolume)}`} tone="good" />
          <ResultRow strong label="Volume you can afford to lose" value={r.tolerableLossPct === null ? "—" : `${r.tolerableLossPct}% (${inr(r.breakEvenUnits * (Number(v.price) * (1 + v.increasePct / 100))) } revenue floor)`} />
        </div>
        <ToolSection title="If demand responds…" testid="p04-sensitivity">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-zinc-500 border-b border-black/10">
                <th className="py-2 font-medium">Elasticity</th>
                <th className="py-2 font-medium">Volume change</th>
                <th className="py-2 font-medium">Total contribution</th>
                <th className="py-2 font-medium">vs today</th>
              </tr>
            </thead>
            <tbody>
              {r.sensitivity.map((s) => (
                <tr key={s.elasticity} className="border-b border-black/5">
                  <td className="py-2">{s.elasticity}</td>
                  <td className="py-2 tabular-nums">{s.volumeChangePct}%</td>
                  <td className="py-2 tabular-nums">{inr(s.total)}</td>
                  <td className={`py-2 tabular-nums ${s.vsBefore >= 0 ? "text-emerald-700" : "text-red-700"}`}>{s.vsBefore >= 0 ? "+" : ""}{inr(s.vsBefore)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-zinc-500">Illustrative elasticities, not a prediction of your customers' response.</p>
        </ToolSection>
      </div>
    </div>
  );
}

export function ToolP09() {
  const [v, set] = useNumbers({ fixedCosts: 500000, price: 1000, variableCost: 600, currentUnits: 1200 });
  const r = useMemo(() => breakEven(v), [v]);
  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-5">
        <Field label="Monthly fixed costs" prefix="₹" value={v.fixedCosts} onChange={set("fixedCosts")} testid="p09-fixed" />
        <Field label="Selling price per unit" prefix="₹" value={v.price} onChange={set("price")} testid="p09-price" />
        <Field label="Variable cost per unit" prefix="₹" value={v.variableCost} onChange={set("variableCost")} testid="p09-cost" />
        <Field label="Current monthly units" value={v.currentUnits} onChange={set("currentUnits")} testid="p09-units" />
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">Break-even position</div>
        <div className="mt-3">
          <ResultRow label="Contribution ratio" value={`${r.contributionRatio}%`} />
          <ResultRow label="Break-even units / month" value={r.breakEvenUnits === null ? "—" : Math.ceil(r.breakEvenUnits).toLocaleString("en-IN")} />
          <ResultRow label="Break-even revenue / month" value={r.breakEvenRevenue === null ? "—" : inr(r.breakEvenRevenue)} />
          <ResultRow strong label="Margin of safety" value={r.marginOfSafetyPct === null ? "—" : `${r.marginOfSafetyPct}%`} tone={r.marginOfSafetyPct !== null && r.marginOfSafetyPct < 15 ? "bad" : "good"} />
        </div>
        {r.warning && <div className="mt-4"><Note tone="warn">{r.warning}</Note></div>}
        <p className="mt-4 text-xs text-zinc-500">Assumes a fixed price/cost per unit and (for mixes) a constant sales mix. Review monthly as costs move.</p>
      </div>
    </div>
  );
}
